import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/ui/searchable-select';
import { INDIAN_STATES } from '@/data/indianCities';
import { apiService } from '@/services/api';
import { AddressVerification } from '@/types/addressVerification';
import AddressSearchField, { PickedPlace } from './AddressSearchField';
import MapPinPicker from './MapPinPicker';

interface AddAddressVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editVerification?: AddressVerification | null;
  mode?: 'digital' | 'vendor';
  // Only super-admins may see/enter the per-case price.
  canSetPrice?: boolean;
}

interface FormData {
  code: string;
  applicantNo: string;
  date: string;
  name: string;
  fathersName: string;
  initiatorName: string;
  phone: string;
  companyName: string;
  email: string;
  address: string;
  presentAddress: string;
  city: string;
  state: string;
  pin: string;
  landmark: string;
  addressType: 'current' | 'permanent' | 'office';
  // Reference point the candidate must be near to submit. Blank => gate off.
  addressLatitude: string;
  addressLongitude: string;
  addressRadiusMeters: string;
  // Google's formatted address for the picked place, stored with the pin.
  addressFormatted: string;
  // How the pin was set — see AddressVerification.addressLocation.source.
  addressSource: '' | 'places' | 'map' | 'device' | 'manual';
  verificationMethod: 'self' | 'physical' | 'document';
  vendor: string;
  vendorWorkPrice: string;
}

interface VendorOption {
  _id: string;
  name: string;
  email: string;
  addressVerificationPrice?: number;
  locations?: string[];
}

// An AddressVerification.vendor may be a populated object or an id string.
const extractVendorId = (vendor: any): string => {
  if (!vendor) return '';
  if (typeof vendor === 'string') return vendor;
  return vendor._id || '';
};

interface FormErrors {
  [key: string]: string;
}

const AddAddressVerificationDialog = ({
  open,
  onClose,
  onSuccess,
  editVerification,
  mode = 'digital',
  canSetPrice = false,
}: AddAddressVerificationDialogProps) => {
  const isVendorMode = mode === 'vendor';
  const [formData, setFormData] = useState<FormData>({
    code: '',
    applicantNo: '',
    date: new Date().toISOString().split('T')[0],
    name: '',
    fathersName: '',
    initiatorName: '',
    phone: '',
    companyName: '',
    email: '',
    address: '',
    presentAddress: '',
    city: '',
    state: '',
    pin: '',
    landmark: '',
    addressType: 'current',
    addressLatitude: '',
    addressLongitude: '',
    addressRadiusMeters: '100',
    addressFormatted: '',
    addressSource: '' as '' | 'places' | 'map' | 'device' | 'manual',
    verificationMethod: 'self',
    vendor: '',
    vendorWorkPrice: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [pinningLocation, setPinningLocation] = useState(false);
  const [pinError, setPinError] = useState('');
  const [showMap, setShowMap] = useState(false);
  // Manual coordinate entry is kept as a fallback: when neither the Places key
  // (backend) nor the Maps key (browser) is configured, search and map are both
  // unavailable and this is the only way left to pin a case's address.
  const [showManualCoords, setShowManualCoords] = useState(false);

  /** Typed coordinates: the pin no longer corresponds to a searched place. */
  const handleManualCoordChange = (field: 'addressLatitude' | 'addressLongitude', value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      addressFormatted: '',
      addressSource: 'manual',
    }));
  };

  /**
   * An address was picked from the search box. Stores the formatted address
   * together with the coordinates it came from, so the two can never disagree.
   *
   * The case's own `address` field is filled only when empty — an operator who
   * already typed a door/flat number should not lose it to Google's version,
   * which usually drops that detail. City/state/pin fill the same way.
   */
  const handlePlacePicked = (place: PickedPlace) => {
    setPinError('');
    setFormData((prev) => ({
      ...prev,
      addressLatitude: place.latitude != null ? String(place.latitude) : '',
      addressLongitude: place.longitude != null ? String(place.longitude) : '',
      addressFormatted: place.formattedAddress || '',
      addressSource: 'places',
      address: prev.address.trim() ? prev.address : (place.formattedAddress || ''),
      city: prev.city.trim() ? prev.city : (place.city || ''),
      state: prev.state.trim() ? prev.state : (place.state || ''),
      pin: prev.pin.trim() ? prev.pin : (place.pin || ''),
    }));
  };

  /**
   * Pin moved on the map.
   *
   * `addressFormatted` is CLEARED rather than kept: it describes the place that
   * was searched for, and once the pin is dragged elsewhere it no longer
   * describes where the pin is. Keeping it would store a formatted address
   * that contradicts its own coordinates -- e.g. search "MG Road", drag 3km to
   * the real house, and the record would still claim MG Road. The operator's
   * own `address` text is left untouched; only the pin's own label is dropped.
   */
  const handleMapPinChange = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      addressLatitude: String(lat),
      addressLongitude: String(lng),
      addressFormatted: '',
      addressSource: 'map',
    }));
  };

  const handleClearPin = () => {
    setPinError('');
    setFormData((prev) => ({
      ...prev,
      addressLatitude: '',
      addressLongitude: '',
      addressFormatted: '',
      addressSource: '',
    }));
  };

  /**
   * Fill the pin from the device's current position. Useful when the case is
   * raised on-site; otherwise the coordinates are typed or pasted from a map.
   */
  const handleUseCurrentLocation = () => {
    setPinError('');
    if (!('geolocation' in navigator)) {
      setPinError('This browser does not support location access.');
      return;
    }
    setPinningLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFormData((prev) => ({
          ...prev,
          addressLatitude: String(pos.coords.latitude),
          addressLongitude: String(pos.coords.longitude),
          addressSource: 'device',
        }));
        setPinningLocation(false);
      },
      (err) => {
        setPinError(err.message || 'Could not get current location.');
        setPinningLocation(false);
      },
      { enableHighAccuracy: true, timeout: 20000 }
    );
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [vendorLocationFilter, setVendorLocationFilter] = useState('');

  // Load the active vendor list once the dialog opens, refetching whenever
  // the location filter changes so the dropdown only shows vendors covering
  // that city.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiService.getVendors({
          isActive: true,
          limit: 200,
          ...(vendorLocationFilter ? { location: vendorLocationFilter } : {}),
        });
        if (!cancelled && response.success && response.data) {
          setVendors(response.data.vendors || []);
        }
      } catch (error) {
        console.error('Failed to load vendors:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, vendorLocationFilter]);

  // Reset the filter each time the dialog opens fresh.
  useEffect(() => {
    if (open) setVendorLocationFilter('');
  }, [open]);

  useEffect(() => {
    if (editVerification && open) {
      setFormData({
        code: editVerification.code || '',
        applicantNo: editVerification.applicantNo || '',
        date: editVerification.formSubmitDate
          ? new Date(editVerification.formSubmitDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        name: editVerification.name || '',
        fathersName: editVerification.fathersName || '',
        initiatorName: editVerification.initiatorName || '',
        phone: editVerification.phone || '',
        companyName: editVerification.companyName || '',
        email: editVerification.email || '',
        address: editVerification.address || '',
        presentAddress: editVerification.presentAddress || '',
        city: editVerification.city || '',
        state: editVerification.state || '',
        pin: editVerification.pin || '',
        landmark: editVerification.landmark || '',
        addressType: editVerification.addressType || 'current',
        addressLatitude: editVerification.addressLocation?.latitude != null ? String(editVerification.addressLocation.latitude) : '',
        addressLongitude: editVerification.addressLocation?.longitude != null ? String(editVerification.addressLocation.longitude) : '',
        addressRadiusMeters: editVerification.addressLocation?.radiusMeters != null ? String(editVerification.addressLocation.radiusMeters) : '100',
        addressFormatted: editVerification.addressLocation?.formattedAddress || '',
        addressSource: (editVerification.addressLocation?.source as any) || '',
        verificationMethod: editVerification.verificationMethod || 'self',
        vendor: extractVendorId((editVerification as any).vendor),
        vendorWorkPrice:
          (editVerification as any).vendorWork?.price != null
            ? String((editVerification as any).vendorWork.price)
            : '',
      });
    } else if (!editVerification && open) {
      setFormData({
        code: '',
        applicantNo: '',
        date: new Date().toISOString().split('T')[0],
        name: '',
        fathersName: '',
        initiatorName: '',
        phone: '',
        companyName: '',
        email: '',
        address: '',
        presentAddress: '',
        city: '',
        state: '',
        pin: '',
        landmark: '',
        addressType: 'current',
        addressLatitude: '',
        addressLongitude: '',
        addressRadiusMeters: '100',
        addressFormatted: '',
        addressSource: '',
        verificationMethod: 'self',
        vendor: '',
        vendorWorkPrice: '',
      });
      setErrors({});
    }
  }, [editVerification, open]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.name.trim()) newErrors.name = 'Candidate name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required';

    if (formData.pin && !/^\d{6}$/.test(formData.pin.replace(/\D/g, ''))) {
      newErrors.pin = 'PIN code must be 6 digits';
    }

    // Vendor tab: vendor is mandatory. Price is super-admin-only (canSetPrice);
    // admins never see it and it defaults to the vendor's rate server-side.
    if (isVendorMode) {
      if (!formData.vendor) newErrors.vendor = 'Vendor is required';
      if (canSetPrice) {
        if (formData.vendorWorkPrice === '') {
          newErrors.vendorWorkPrice = 'Price is required';
        } else {
          const p = Number(formData.vendorWorkPrice);
          if (Number.isNaN(p) || p < 0) newErrors.vendorWorkPrice = 'Price must be a number ≥ 0';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const {
        vendor, vendorWorkPrice,
        addressLatitude, addressLongitude, addressRadiusMeters,
        addressFormatted, addressSource,
        ...rest
      } = formData;
      const submitData: any = {
        ...rest,
        formSubmitDate: new Date(formData.date).toISOString(),
      };

      // Fold the three pin inputs into the nested shape the API expects. Both
      // coordinates must be present and numeric, otherwise the pin is sent as
      // null so the proximity gate stays off rather than half-configured.
      const pinLat = parseFloat(addressLatitude);
      const pinLng = parseFloat(addressLongitude);
      submitData.addressLocation =
        Number.isFinite(pinLat) && Number.isFinite(pinLng)
          ? {
              latitude: pinLat,
              longitude: pinLng,
              radiusMeters: Number(addressRadiusMeters) || 100,
              formattedAddress: addressFormatted || undefined,
              // Left undefined when genuinely unknown (a legacy case pinned
              // before this field existed). Coercing to 'manual' here would
              // falsify the audit trail on any unrelated edit of that case.
              source: addressSource || undefined,
            }
          : null;
      if (isVendorMode) {
        // Vendor tab: attach vendor; only super-admin sends a per-case price.
        submitData.vendor = vendor || null;
        if (canSetPrice) {
          submitData.vendorWorkPrice = vendorWorkPrice === '' ? null : Number(vendorWorkPrice);
        }
      } else if (editVerification) {
        // Digital tab editing: preserve whatever vendor state exists, don't force it.
        submitData.vendor = vendor || null;
      }
      // Digital create: omit vendor entirely (stays unassigned → Digital tab).

      let response;
      if (editVerification?._id) {
        response = await apiService.updateAddressVerification(editVerification._id, submitData);
      } else {
        response = await apiService.createAddressVerification(submitData);
      }

      if (response.success) {
        alert(
          editVerification
            ? 'Verification updated successfully!'
            : 'Verification created successfully!'
        );
        onSuccess();
      } else {
        alert(response.message || 'Failed to save verification');
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      alert(error.message || 'Failed to save verification');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editVerification ? 'Edit Address Verification' : 'Add Address Verification'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Code */}
            <div>
              <Label htmlFor="code">
                Code (BGVID) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                placeholder="e.g., AV001"
              />
              {errors.code && <p className="text-sm text-red-500 mt-1">{errors.code}</p>}
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="date">
                Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
              />
              {errors.date && <p className="text-sm text-red-500 mt-1">{errors.date}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Applicant No */}
            <div>
              <Label htmlFor="applicantNo">Applicant No</Label>
              <Input
                id="applicantNo"
                value={formData.applicantNo}
                onChange={(e) => handleInputChange('applicantNo', e.target.value)}
                placeholder="e.g., ART-BGV-026"
              />
            </div>

            {/* Fathers Name */}
            <div>
              <Label htmlFor="fathersName">Father's Name</Label>
              <Input
                id="fathersName"
                value={formData.fathersName}
                onChange={(e) => handleInputChange('fathersName', e.target.value)}
                placeholder="Father's name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Candidate Name */}
            <div>
              <Label htmlFor="name">
                Candidate Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Full name"
              />
              {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
            </div>

            {/* Initiator Name */}
            <div>
              <Label htmlFor="initiatorName">Initiator Name</Label>
              <Input
                id="initiatorName"
                value={formData.initiatorName}
                onChange={(e) => handleInputChange('initiatorName', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Phone */}
            <div>
              <Label htmlFor="phone">
                Phone <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="10-digit mobile"
              />
              {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="candidate@example.com"
              />
              {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Company Name */}
          <div>
            <Label htmlFor="companyName">
              Company Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="companyName"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              placeholder="Organization name"
            />
            {errors.companyName && (
              <p className="text-sm text-red-500 mt-1">{errors.companyName}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <Label htmlFor="address">
              Address <span className="text-red-500">*</span>
            </Label>
            <textarea
              id="address"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="Full address"
            />
            {errors.address && <p className="text-sm text-red-500 mt-1">{errors.address}</p>}
          </div>

          {/* Present Address */}
          <div>
            <Label htmlFor="presentAddress">Present Address</Label>
            <textarea
              id="presentAddress"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              value={formData.presentAddress}
              onChange={(e) => handleInputChange('presentAddress', e.target.value)}
              placeholder="Current residential address (optional)"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* City */}
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="City"
              />
            </div>

            {/* State */}
            <div>
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="State"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* PIN Code */}
            <div>
              <Label htmlFor="pin">PIN Code</Label>
              <Input
                id="pin"
                value={formData.pin}
                onChange={(e) => handleInputChange('pin', e.target.value)}
                placeholder="6-digit PIN"
                maxLength={6}
              />
              {errors.pin && <p className="text-sm text-red-500 mt-1">{errors.pin}</p>}
            </div>

            {/* Landmark */}
            <div>
              <Label htmlFor="landmark">Landmark</Label>
              <Input
                id="landmark"
                value={formData.landmark}
                onChange={(e) => handleInputChange('landmark', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Address Type */}
            <div>
              <Label htmlFor="addressType">Address Type</Label>
              <select
                id="addressType"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.addressType}
                onChange={(e) =>
                  handleInputChange('addressType', e.target.value as FormData['addressType'])
                }
              >
                <option value="current">Current Address</option>
                <option value="permanent">Permanent Address</option>
                <option value="office">Office Address</option>
              </select>
            </div>

            {/* Address GPS pin — drives the candidate's proximity gate */}
            <div className="col-span-2 border rounded-md p-3 bg-gray-50 space-y-3">
              <div>
                <Label className="font-medium">Address Location (optional)</Label>
                <p className="text-xs text-gray-600 mt-1">
                  Search for the address to store its exact location. When set, the candidate
                  can only open and submit the form within the radius below. Leave blank to
                  allow submission from anywhere.
                </p>
              </div>

              <AddressSearchField
                onPick={handlePlacePicked}
                pinnedLabel={
                  // Mirrors the submit-time Number.isFinite guard: a
                  // non-numeric value must not render "Pinned (NaN, NaN)"
                  // while submit correctly sends no pin at all.
                  Number.isFinite(Number(formData.addressLatitude)) &&
                  Number.isFinite(Number(formData.addressLongitude)) &&
                  formData.addressLatitude !== '' && formData.addressLongitude !== ''
                    ? `Pinned${formData.addressFormatted ? `: ${formData.addressFormatted}` : ''} (${Number(formData.addressLatitude).toFixed(6)}, ${Number(formData.addressLongitude).toFixed(6)})`
                    : undefined
                }
              />

              <div className="flex flex-wrap items-end gap-3">
                <div className="w-32">
                  <Label htmlFor="addressRadiusMeters" className="text-xs">Radius (m)</Label>
                  <select
                    id="addressRadiusMeters"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.addressRadiusMeters}
                    onChange={(e) => handleInputChange('addressRadiusMeters', e.target.value)}
                  >
                    <option value="50">50 m</option>
                    <option value="75">75 m</option>
                    <option value="100">100 m</option>
                  </select>
                </div>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline disabled:text-gray-400 pb-2"
                  onClick={() => setShowMap((v) => !v)}
                >
                  {showMap ? 'Hide map' : 'Adjust pin on map'}
                </button>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline pb-2"
                  onClick={() => setShowManualCoords((v) => !v)}
                >
                  {showManualCoords ? 'Hide coordinates' : 'Enter coordinates'}
                </button>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline disabled:text-gray-400 pb-2"
                  disabled={pinningLocation}
                  onClick={handleUseCurrentLocation}
                >
                  {pinningLocation ? 'Getting current location…' : 'Use my current location'}
                </button>
                {(formData.addressLatitude || formData.addressLongitude) && (
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline pb-2"
                    onClick={handleClearPin}
                  >
                    Clear pin
                  </button>
                )}
              </div>

              {showManualCoords && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="addressLatitude" className="text-xs">Latitude</Label>
                    <Input
                      id="addressLatitude"
                      type="number"
                      step="any"
                      placeholder="12.971599"
                      value={formData.addressLatitude}
                      onChange={(e) => handleManualCoordChange('addressLatitude', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="addressLongitude" className="text-xs">Longitude</Label>
                    <Input
                      id="addressLongitude"
                      type="number"
                      step="any"
                      placeholder="77.594566"
                      value={formData.addressLongitude}
                      onChange={(e) => handleManualCoordChange('addressLongitude', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {showMap && (
                <MapPinPicker
                  latitude={formData.addressLatitude ? Number(formData.addressLatitude) : undefined}
                  longitude={formData.addressLongitude ? Number(formData.addressLongitude) : undefined}
                  radiusMeters={Number(formData.addressRadiusMeters) || 100}
                  onChange={handleMapPinChange}
                />
              )}

              {pinError && <p className="text-xs text-red-600">{pinError}</p>}
            </div>

            {/* Verification Method */}
            <div>
              <Label htmlFor="verificationMethod">Verification Method</Label>
              <select
                id="verificationMethod"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.verificationMethod}
                onChange={(e) =>
                  handleInputChange(
                    'verificationMethod',
                    e.target.value as FormData['verificationMethod']
                  )
                }
              >
                <option value="self">Self Verification</option>
                <option value="physical">Physical Verification</option>
                <option value="document">Document Verification</option>
              </select>
            </div>
          </div>

          {/* Assign Vendor + price — Vendor tab only; price is super-admin-only */}
          {isVendorMode && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="vendor-location-filter">Filter Vendors by Location</Label>
                <SearchableSelect
                  options={INDIAN_STATES}
                  value={vendorLocationFilter}
                  onChange={setVendorLocationFilter}
                  placeholder="All locations"
                  allLabel="All locations"
                  searchPlaceholder="Search states…"
                />
              </div>
              <div className={`grid grid-cols-1 gap-4 ${canSetPrice ? 'sm:grid-cols-2' : ''}`}>
              <div>
                <Label htmlFor="vendor">
                  Assign Vendor <span className="text-red-500">*</span>
                </Label>
                <select
                  id="vendor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.vendor}
                  onChange={(e) => {
                    const vid = e.target.value;
                    const v = vendors.find((x) => x._id === vid);
                    setFormData((prev) => ({
                      ...prev,
                      vendor: vid,
                      // Pre-fill price from the vendor's default when empty.
                      vendorWorkPrice:
                        prev.vendorWorkPrice === '' && v?.addressVerificationPrice != null
                          ? String(v.addressVerificationPrice)
                          : prev.vendorWorkPrice,
                    }));
                    if (errors.vendor) setErrors((p) => ({ ...p, vendor: '' }));
                  }}
                >
                  <option value="">— Select vendor —</option>
                  {vendors.map((v) => (
                    <option key={v._id} value={v._id}>
                      {v.name} ({v.email})
                    </option>
                  ))}
                </select>
                {errors.vendor && <p className="text-sm text-red-500 mt-1">{errors.vendor}</p>}
              </div>
              {canSetPrice && (
                <div>
                  <Label htmlFor="vendorWorkPrice">
                    Price per case (₹) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="vendorWorkPrice"
                    type="number"
                    min={0}
                    step="0.01"
                    value={formData.vendorWorkPrice}
                    onChange={(e) => handleInputChange('vendorWorkPrice', e.target.value)}
                    placeholder="e.g., 250"
                  />
                  {errors.vendorWorkPrice && (
                    <p className="text-sm text-red-500 mt-1">{errors.vendorWorkPrice}</p>
                  )}
                </div>
              )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : editVerification ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddAddressVerificationDialog;
